import { apiRequest } from "../../../../shared/lib/api";
import { normalizeSearchText } from "../../../../shared/utils/searchText";
import {
  AuthPayload,
  CategoryChangeRequest,
  CategoryChangeRequestStatus,
  AuthUser,
  CreateUserPayload,
  OrganizerRankingSettings,
  OrganizerRankingPointsTable,
  OrganizerVisualIdentityColors,
  OrganizerVisualIdentityBackgroundMode,
  OrganizerVisualIdentitySettings,
  normalizeUserCategory,
  normalizeUserSidePreference,
  normalizeUserSex,
  UpdateUserPayload,
  UserSidePreference,
} from "../types/auth";

function normalizeAuthUserPayload<T extends AuthUser | null | undefined>(
  user: T,
): T {
  if (!user) return user;
  const raw = user as AuthUser & { avatar_url?: string | null };
  const createdAt = String(
    raw.createdAt ||
      raw.created_at ||
      raw.createdDate ||
      raw.registrationDate ||
      raw.memberSince ||
      "",
  ).trim();
  const rawClubId = (raw as { clubId?: unknown }).clubId;
  return {
    ...raw,
    clubId:
      rawClubId === null || rawClubId === undefined || rawClubId === ""
        ? null
        : Number(rawClubId),
    clubName: String(raw.clubName || "").trim(),
    clubSlug: String(raw.clubSlug || "").trim(),
    clubLogoUrl: String(raw.clubLogoUrl || "").trim(),
    avatarUrl: String(raw.avatarUrl || raw.avatar_url || "").trim(),
    createdAt,
    created_at: String(raw.created_at || createdAt).trim(),
  } as T;
}

function normalizeAuthPayload(payload: AuthPayload) {
  return {
    ...payload,
    user: normalizeAuthUserPayload(payload.user),
  };
}

export function login(identifier: string, password: string) {
  return apiRequest<AuthPayload>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ identifier, password }),
  }).then(normalizeAuthPayload);
}

export function requestPasswordReset(identifier: string) {
  return apiRequest<{ ok: boolean; message?: string }>("/api/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ identifier }),
  });
}

export function resetPassword(token: string, newPassword: string) {
  return apiRequest<{ ok: boolean; message?: string }>("/api/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, newPassword }),
  });
}

export function register(
  name: string,
  email: string,
  password: string,
  cpf: string,
  phone = "",
  sex: AuthUser["sex"] = "",
  category: AuthUser["category"] = "",
) {
  return apiRequest<AuthPayload>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({
      name,
      email,
      password,
      cpf: String(cpf || "").trim(),
      phone: String(phone || "").trim(),
      sex: normalizeUserSex(sex),
      category: normalizeUserCategory(category),
    }),
  }).then(normalizeAuthPayload);
}

export function me(token: string) {
  return apiRequest<AuthUser>("/api/auth/me", {
    headers: { Authorization: `Bearer ${token}` },
  }).then(normalizeAuthUserPayload);
}

export function logout(token: string) {
  return apiRequest<{ ok: boolean }>("/api/auth/logout", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function completeProfile(token: string, cpf: string, phone = "") {
  return apiRequest<AuthUser>("/api/auth/complete-profile", {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ cpf, phone }),
  }).then(normalizeAuthUserPayload);
}

export function getPlayerSidePreference(token: string, cpf: string) {
  return apiRequest<{ cpf: string; name: string; sidePref?: string }>(
    `/api/players/${encodeURIComponent(String(cpf || ""))}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  ).then((player) => normalizeUserSidePreference(player.sidePref));
}

export function updatePlayerSidePreference(
  token: string,
  cpf: string,
  name: string,
  sidePref: UserSidePreference,
) {
  const normalizedSidePref = normalizeUserSidePreference(sidePref) || "ambos";
  return apiRequest<{ cpf: string; name: string }>(
    "/api/players",
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        cpf: String(cpf || ""),
        name: String(name || "").trim(),
        sidePref: normalizedSidePref,
      }),
    },
  ).then(() => normalizedSidePref);
}

export function updateMyProfile(
  token: string,
  payload: Partial<
    Pick<
      AuthUser,
      | "name"
      | "email"
      | "cpf"
      | "phone"
      | "sex"
      | "category"
      | "clubId"
      | "avatarUrl"
    >
  >
) {
  const normalizedPayload = {
    ...payload,
    ...(payload?.sex !== undefined ? { sex: normalizeUserSex(payload.sex) } : {}),
    ...(payload?.category !== undefined
      ? { category: normalizeUserCategory(payload.category) }
      : {}),
    ...(payload?.clubId !== undefined
      ? {
          clubId:
            payload.clubId === null || Number(payload.clubId) <= 0
              ? null
              : Math.floor(Number(payload.clubId)),
        }
      : {}),
  };
  return apiRequest<AuthUser>("/api/auth/account", {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(normalizedPayload),
  }).then(normalizeAuthUserPayload);
}

export function changeMyPassword(
  token: string,
  currentPassword: string,
  newPassword: string,
) {
  return apiRequest<{ ok: boolean; message?: string }>("/api/auth/account/password", {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export function confirmEmailChange(token: string) {
  return apiRequest<{ ok: boolean; message?: string }>("/api/auth/confirm-email", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export function requestEmailConfirmationLink(token: string) {
  return apiRequest<{
    ok: boolean;
    pendingEmail?: string;
    emailChangeConfirmationLink?: string;
    emailChangeExpiresAt?: string;
  }>("/api/auth/confirm-email/request", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function getOrganizerRankingSettings(token: string) {
  return apiRequest<OrganizerRankingSettings>(
    "/api/auth/organizer-ranking-settings",
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function saveOrganizerRankingSettings(
  token: string,
  payload: { mode: "tnp_standard" | "custom"; points?: OrganizerRankingPointsTable },
) {
  return apiRequest<OrganizerRankingSettings>(
    "/api/auth/organizer-ranking-settings",
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    },
  );
}

export function duplicateOrganizerRankingOfficial(token: string) {
  return apiRequest<OrganizerRankingSettings>(
    "/api/auth/organizer-ranking-settings/duplicate-official",
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function resetOrganizerRankingSettings(token: string) {
  return apiRequest<OrganizerRankingSettings>(
    "/api/auth/organizer-ranking-settings/reset",
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function getOrganizerVisualIdentity(token: string) {
  return apiRequest<OrganizerVisualIdentitySettings>(
    "/api/auth/organizer-visual-identity",
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function saveOrganizerVisualIdentity(
  token: string,
  payload: {
    mode: "tnp_standard" | "custom";
    colors?: OrganizerVisualIdentityColors;
    logoUrl?: string | null;
    displayName?: string | null;
    backgroundMode?: OrganizerVisualIdentityBackgroundMode;
  },
) {
  return apiRequest<OrganizerVisualIdentitySettings>(
    "/api/auth/organizer-visual-identity",
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    },
  );
}

export function resetOrganizerVisualIdentity(token: string) {
  return apiRequest<OrganizerVisualIdentitySettings>(
    "/api/auth/organizer-visual-identity/reset",
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function getPublicOrganizerVisualIdentity(organizerUserId: number) {
  return apiRequest<OrganizerVisualIdentitySettings>(
    `/api/public/organizers/${organizerUserId}/visual-identity`,
  );
}

export function requestCategoryChange(
  token: string,
  payload: {
    requestedCategory: AuthUser["category"];
    reason?: string;
  },
) {
  return apiRequest<{ ok: boolean; request: CategoryChangeRequest }>(
    "/api/auth/category-change-requests",
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        requestedCategory: normalizeUserCategory(payload?.requestedCategory),
        reason: String(payload?.reason || "").trim(),
      }),
    },
  );
}

export function listMyCategoryChangeRequests(
  token: string,
  params?: { status?: CategoryChangeRequestStatus | "all" },
) {
  const query = new URLSearchParams();
  if (params?.status) query.set("status", params.status);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return apiRequest<CategoryChangeRequest[]>(
    `/api/auth/category-change-requests/mine${suffix}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function listCategoryChangeRequests(
  token: string,
  params?: { status?: CategoryChangeRequestStatus | "all" },
) {
  const query = new URLSearchParams();
  if (params?.status) query.set("status", params.status);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return apiRequest<CategoryChangeRequest[]>(
    `/api/admin/category-change-requests${suffix}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export function resolveCategoryChangeRequest(
  token: string,
  id: number,
  payload: {
    action: "approve" | "reject";
    adminNote?: string;
  },
) {
  return apiRequest<{ ok: boolean; request: CategoryChangeRequest }>(
    `/api/admin/category-change-requests/${id}`,
    {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        action: payload.action,
        adminNote: String(payload.adminNote || "").trim(),
      }),
    },
  );
}

export function listUsers(token: string) {
  return apiRequest<AuthUser[]>("/api/users", {
    headers: { Authorization: `Bearer ${token}` },
  }).then((users) => users.map((user) => normalizeAuthUserPayload(user)));
}

export type OrganizerDirectoryItem = {
  id: number;
  name: string;
  sex?: "male" | "female" | "other" | null;
  avatarUrl?: string | null;
  logoUrl?: string | null;
  visualIdentityMode?: "tnp_standard" | "custom";
  backgroundMode?: OrganizerVisualIdentityBackgroundMode;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  accentColor?: string | null;
  athletesCount?: number | null;
  playersCount?: number | null;
  eventsCount?: number | null;
  updatedAt?: string | null;
};

export type OrganizerDirectoryResponse = {
  items: OrganizerDirectoryItem[];
  total: number;
  nextOffset: number | null;
  limit: number;
  offset: number;
  query: string;
};

type ListOrganizersParams = {
  q?: string;
  limit?: number;
  offset?: number;
};

export async function listOrganizers(params?: ListOrganizersParams) {
  const queryValue = normalizeSearchText(params?.q || "");
  const limitValue = Number.isFinite(params?.limit)
    ? Math.max(1, Math.min(50, Math.floor(Number(params?.limit))))
    : 20;
  const offsetValue = Number.isFinite(params?.offset)
    ? Math.max(0, Math.floor(Number(params?.offset)))
    : 0;

  const paginate = (rawItems: OrganizerDirectoryItem[]) => {
    const filtered = (rawItems || [])
      .map((item) => ({
        id: Number(item?.id || 0),
        name: String(item?.name || "").trim(),
        sex:
          item?.sex === "male" ||
          item?.sex === "female" ||
          item?.sex === "other"
            ? item.sex
            : null,
        avatarUrl: String(item?.avatarUrl || item?.logoUrl || "").trim() || null,
        logoUrl: String(item?.logoUrl || item?.avatarUrl || "").trim() || null,
        visualIdentityMode:
          item?.visualIdentityMode === "custom" ? "custom" : "tnp_standard",
        primaryColor: String(item?.primaryColor || "").trim() || null,
        secondaryColor: String(item?.secondaryColor || "").trim() || null,
        accentColor: String(item?.accentColor || "").trim() || null,
        backgroundMode:
          item?.backgroundMode === "black" || item?.backgroundMode === "white"
            ? item.backgroundMode
            : "blue",
        athletesCount: Number(item?.athletesCount ?? item?.playersCount ?? 0),
        playersCount: Number(item?.playersCount ?? item?.athletesCount ?? 0),
        eventsCount: Number(item?.eventsCount ?? 0),
        updatedAt: String(item?.updatedAt || "").trim() || null,
      }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0 && item.name)
      .filter((item) =>
        queryValue ? normalizeSearchText(item.name).includes(queryValue) : true,
      )
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    const items = filtered.slice(offsetValue, offsetValue + limitValue);
    const nextOffset =
      offsetValue + limitValue < filtered.length ? offsetValue + limitValue : null;
    return {
      items,
      total: filtered.length,
      nextOffset,
      limit: limitValue,
      offset: offsetValue,
      query: queryValue,
    } as OrganizerDirectoryResponse;
  };

  const query = new URLSearchParams();
  if (params?.q) query.set("q", params.q);
  if (Number.isFinite(params?.limit)) query.set("limit", String(params?.limit));
  if (Number.isFinite(params?.offset)) {
    query.set("offset", String(params?.offset));
  }
  const suffix = query.toString() ? `?${query.toString()}` : "";
  // Public organizers directory (all organizers, even without challenges/ranking).
  let response: OrganizerDirectoryItem[] | OrganizerDirectoryResponse;
  try {
    response = await apiRequest<OrganizerDirectoryItem[] | OrganizerDirectoryResponse>(
      `/api/public/organizers${suffix}`,
    );
  } catch (_publicRouteError) {
    response = await apiRequest<OrganizerDirectoryItem[] | OrganizerDirectoryResponse>(
      `/api/organizers${suffix}`,
    );
  }
  if (Array.isArray(response)) {
    return paginate(response);
  }
  return {
    ...response,
    query: String(response?.query || queryValue),
  };
}

export async function listOrganizersDirectory(
  token?: string,
  params?: ListOrganizersParams,
) {
  try {
    const response = await listOrganizers(params);
    return response;
  } catch (primaryError) {
    if (!token) throw primaryError;
    const users = await listUsers(token);
    const q = normalizeSearchText(params?.q || "");
    const limit = Number.isFinite(params?.limit)
      ? Math.max(1, Math.min(50, Math.floor(Number(params?.limit))))
      : 20;
    const offset = Number.isFinite(params?.offset)
      ? Math.max(0, Math.floor(Number(params?.offset)))
      : 0;

    const filtered = (users || [])
      .filter((user) => {
        const role = String(user.role || "")
          .trim()
          .toLowerCase();
        if (!(role === "organizer" || role === "organizador")) return false;
        if (!q) return true;
        return normalizeSearchText(user.name).includes(q);
      })
      .map((user) => ({
        id: Number(user.id || 0),
        name: String(user.name || "").trim(),
        sex: user.sex || null,
        avatarUrl: String(user.avatarUrl || "").trim() || null,
      }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0 && item.name)
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));

    const items = filtered.slice(offset, offset + limit);
    const nextOffset = offset + limit < filtered.length ? offset + limit : null;
    return {
      items,
      total: filtered.length,
      nextOffset,
      limit,
      offset,
      query: q,
    } as OrganizerDirectoryResponse;
  }
}

export function createUser(token: string, payload: CreateUserPayload) {
  const normalizedPayload: CreateUserPayload = {
    ...payload,
    sex: normalizeUserSex(payload?.sex),
    category: normalizeUserCategory(payload?.category),
    clubId:
      payload?.clubId === null || payload?.clubId === undefined || Number(payload.clubId) <= 0
        ? null
        : Math.floor(Number(payload.clubId)),
  };
  return apiRequest<AuthUser>("/api/users", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(normalizedPayload),
  }).then(normalizeAuthUserPayload);
}

export function updateUser(token: string, id: number, payload: UpdateUserPayload) {
  const normalizedPayload: UpdateUserPayload = {
    name: String(payload?.name ?? "").trim(),
    email: String(payload?.email ?? "").trim(),
    role:
      payload?.role === "admin"
        ? "admin"
        : payload?.role === "organizer"
        ? "organizer"
        : "user",
    cpf: String(payload?.cpf ?? ""),
    phone: String(payload?.phone ?? ""),
    sex: normalizeUserSex(payload?.sex),
    category: normalizeUserCategory(payload?.category),
    ...(payload?.clubId !== undefined
      ? {
          clubId:
            payload.clubId === null || Number(payload.clubId) <= 0
              ? null
              : Math.floor(Number(payload.clubId)),
        }
      : {}),
    ...(payload?.avatarUrl !== undefined
      ? { avatarUrl: String(payload.avatarUrl || "") }
      : {}),
    ...(payload?.password !== undefined
      ? { password: String(payload.password) }
      : {}),
  };

  return apiRequest<AuthUser>(`/api/users/${id}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(normalizedPayload),
  }).then(normalizeAuthUserPayload);
}

export function deleteUser(token: string, id: number) {
  return apiRequest<{ ok: boolean }>(`/api/users/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
}
